import React, { useState, useEffect, useMemo } from 'react';
import { getApiUrl } from '@/config/api';

interface Team {
  id: number;
  name: string;
}

interface InviteUserModalProps {
  onClose: () => void;
  defaultRole?: 'athlete' | 'coach';
  onSuccess?: () => void;
}

interface InviteResult {
  email: string;
  status: 'invited' | 'added' | 'already_member' | 'error';
  message?: string;
  warning?: string;
  email_sent?: boolean;
}

interface BulkInviteResponse {
  results: InviteResult[];
  counts: Record<string, number>;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Parse a paste-textarea blob into a deduped list of trimmed lowercase emails.
 * Splits on commas, semicolons, and whitespace so coaches can paste from
 * spreadsheets, contact apps, Slack messages, etc. without reformatting.
 */
function parseEmailBlob(blob: string): { valid: string[]; invalid: string[] } {
  const tokens = blob
    .split(/[\s,;]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const seen = new Set<string>();
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const t of tokens) {
    if (seen.has(t)) continue;
    seen.add(t);
    if (EMAIL_RE.test(t)) valid.push(t);
    else invalid.push(t);
  }
  return { valid, invalid };
}

const STATUS_LABEL: Record<InviteResult['status'], string> = {
  invited: 'Invitation sent',
  added: 'Added to team',
  already_member: 'Already a member',
  error: 'Failed',
};

const STATUS_ICON: Record<InviteResult['status'], string> = {
  invited: '✓',
  added: '↩',
  already_member: '•',
  error: '✗',
};

const STATUS_CLASS: Record<InviteResult['status'], string> = {
  invited: 'text-emerald-600 dark:text-emerald-400',
  added: 'text-emerald-600 dark:text-emerald-400',
  already_member: 'cgm-text-muted',
  error: 'text-red-600 dark:text-red-400',
};

const InviteUserModal: React.FC<InviteUserModalProps> = ({ onClose, defaultRole, onSuccess }) => {
  const [emailBlob, setEmailBlob] = useState('');
  const [role, setRole] = useState<'athlete' | 'coach'>(defaultRole || 'athlete');
  const [teamId, setTeamId] = useState<number | ''>('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState<InviteResult[] | null>(null);

  useEffect(() => {
    const fetchTeams = async () => {
      const token = localStorage.getItem('access');
      if (!token) {
        setTeamsLoading(false);
        return;
      }
      try {
        const res = await fetch(getApiUrl('/api/teams/teams/'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (res.ok) {
          const data = await res.json();
          const rawTeams = Array.isArray(data)
            ? data
            : (data.results || data.data || []);
          const teamList: Team[] = (Array.isArray(rawTeams) ? rawTeams : []).map((t: any) => ({
            id: t.id,
            name: t.name,
          }));
          setTeams(teamList);
          if (teamList.length === 1) {
            setTeamId(teamList[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err);
      } finally {
        setTeamsLoading(false);
      }
    };
    fetchTeams();
  }, []);

  const parsed = useMemo(() => parseEmailBlob(emailBlob), [emailBlob]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    if (parsed.valid.length === 0) {
      setError('Enter at least one valid email address');
      return;
    }
    if (!teamId) {
      setError('Please select a team');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access');
      const res = await fetch(getApiUrl('/api/teams/admin/bulk-invite-users/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ emails: parsed.valid, role, team_id: teamId }),
      });

      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await res.json();
          throw new Error(data.error || data.detail || `Request failed (${res.status})`);
        }
        throw new Error(`Request failed (${res.status})`);
      }

      const data = (await res.json()) as BulkInviteResponse;
      // Prepend client-side invalid addresses so the user sees them in the same list
      const invalidResults: InviteResult[] = parsed.invalid.map((email) => ({
        email,
        status: 'error',
        message: 'Invalid email address',
      }));
      setResults([...invalidResults, ...data.results]);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderResults = () => {
    if (!results) return null;
    const counts = results.reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});
    const summaryParts: string[] = [];
    if (counts.invited) summaryParts.push(`${counts.invited} invited`);
    if (counts.added) summaryParts.push(`${counts.added} added`);
    if (counts.already_member) summaryParts.push(`${counts.already_member} already members`);
    if (counts.error) summaryParts.push(`${counts.error} failed`);

    return (
      <div className="space-y-3">
        <div className="cgm-alert-success px-4 py-3 rounded-md text-sm">
          {summaryParts.join(' · ') || 'No results'}
        </div>
        <ul className="max-h-64 overflow-y-auto divide-y cgm-border-primary text-sm">
          {results.map((r, idx) => (
            <li key={`${r.email}-${idx}`} className="flex items-start gap-3 py-2">
              <span className={`flex-shrink-0 w-5 text-center font-bold ${STATUS_CLASS[r.status]}`}>
                {STATUS_ICON[r.status]}
              </span>
              <div className="min-w-0 flex-1">
                <div className="cgm-text-primary truncate">{r.email}</div>
                <div className={`text-xs ${STATUS_CLASS[r.status]}`}>
                  {r.message || STATUS_LABEL[r.status]}
                  {r.warning ? ` — ${r.warning}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <button
          onClick={onClose}
          className="cgm-button w-full py-3 px-4 flex items-center justify-center"
        >
          Done
        </button>
      </div>
    );
  };

  const validCount = parsed.valid.length;
  const invalidCount = parsed.invalid.length;
  const submitDisabled = loading || teamsLoading || teams.length === 0 || validCount === 0;

  return (
    <div className="cgm-modal-backdrop">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card cgm-modal-panel p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <h3 className="text-2xl cgm-text-primary mb-2">
              Invite {defaultRole === 'coach' ? 'Coaches' : defaultRole === 'athlete' ? 'Athletes' : 'Users'}
            </h3>
            <p className="cgm-text-secondary text-sm">
              Paste one or more emails — separated by commas, spaces, or new lines
            </p>
          </div>

          {error && (
            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
              {error}
            </div>
          )}

          {results ? (
            renderResults()
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="invite-emails"
                  className="block text-sm font-medium cgm-text-secondary mb-1"
                >
                  Email Addresses
                </label>
                <textarea
                  id="invite-emails"
                  required
                  rows={6}
                  value={emailBlob}
                  onChange={(e) => setEmailBlob(e.target.value)}
                  disabled={loading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
                  placeholder={'jane@example.com\njohn@example.com\nsam@example.com'}
                />
                {(validCount > 0 || invalidCount > 0) && (
                  <p className="text-xs cgm-text-muted mt-1">
                    {validCount} valid email{validCount === 1 ? '' : 's'} detected
                    {invalidCount > 0 ? ` · ${invalidCount} invalid (will be skipped)` : ''}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="invite-role"
                  className="block text-sm font-medium cgm-text-secondary mb-1"
                >
                  Role
                </label>
                <select
                  id="invite-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'athlete' | 'coach')}
                  disabled={loading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                </select>
              </div>

              {teams.length > 1 && (
                <div>
                  <label
                    htmlFor="invite-team"
                    className="block text-sm font-medium cgm-text-secondary mb-1"
                  >
                    Team
                  </label>
                  <select
                    id="invite-team"
                    value={teamId}
                    onChange={(e) => setTeamId(Number(e.target.value))}
                    disabled={loading || teamsLoading}
                    className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a team</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {teams.length === 1 && (
                <div>
                  <label className="block text-sm font-medium cgm-text-secondary mb-1">
                    Team
                  </label>
                  <div className="cgm-input w-full px-3 py-2 opacity-70">
                    {teams[0].name}
                  </div>
                </div>
              )}

              {teams.length === 0 && !teamsLoading && (
                <div className="cgm-alert-error px-4 py-3 rounded-md text-sm">
                  No teams found. Please create a team first.
                </div>
              )}

              <button
                type="submit"
                disabled={submitDisabled}
                className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                    Sending {validCount > 1 ? `${validCount} invitations` : 'invitation'}...
                  </>
                ) : (
                  validCount > 1 ? `Send ${validCount} Invitations` : 'Send Invitation'
                )}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
